import {
  applyAnimationEffect as applyEffect,
  changeAnimationEffectPreset as changeEffectPreset,
  ConflictingPresetError,
  DuplicatePresetError,
  getAnimationPreset,
  isAnimationPresetId,
  PresetRoleError,
} from 'adflow';
import type { Animation, AnimationEffect } from '@adflow/types';

import type { AnimationOperation } from '../operations';
import {
  mcpToolResult,
  toolError,
  toolSuccess,
  type TemplateWebMCPFailure,
  type TemplateWebMCPToolResult,
} from '../responses';
import { readWorkingRevision } from '../revision';
import type { EditorStoreLike } from '../toolContext';
import { validateEffectParams, type EffectParams } from './params';
import {
  animationWarnings,
  describeEffect,
  elementNames,
  readAnimation,
  type AnimationEffectSummary,
} from './read';

type AnimationActions = {
  applyAnimationEffect: (targetId: string, presetId: string, params?: EffectParams) => void;
  updateAnimationEffect: (effectId: string, params: EffectParams) => void;
  changeAnimationEffectPreset: (effectId: string, presetId: string) => void;
  removeAnimationEffect: (effectId: string) => void;
  /** Makes several writes one undo step. */
  runHistoryBatch?: <T>(fn: () => T) => T;
};

export type AnimationApplyData = {
  operation: AnimationOperation;
  workingRevision: number;
  effect?: AnimationEffectSummary;
  replaced?: AnimationEffectSummary[];
  removedEffectId?: string;
  animationCleared?: boolean;
  warnings?: string[];
};

type Outcome = Omit<AnimationApplyData, 'operation' | 'workingRevision' | 'warnings'>;

/** A validated change: `commit` writes through store actions, `describe` reads the result. */
type Plan = {
  commit: (actions: AnimationActions) => void;
  describe: (after: Animation | null, names: Map<string, string>) => Outcome;
};

type Failure = { failure: TemplateWebMCPFailure };

function fail(...args: Parameters<typeof toolError>): Failure {
  return { failure: toolError(...args) };
}

function presetErrorFailure(error: unknown): Failure | null {
  if (error instanceof DuplicatePresetError) {
    return fail(
      'ANIMATION_DUPLICATE',
      `${error.presetId} is already applied to ${error.targetId}.`,
      {
        presetId: error.presetId,
        targetId: error.targetId,
      },
    );
  }
  if (error instanceof ConflictingPresetError) {
    return fail(
      'ANIMATION_CONFLICT',
      `${error.presetId} conflicts with another effect on ${error.targetId}. Remove that effect first.`,
      { presetId: error.presetId, targetId: error.targetId },
    );
  }
  if (error instanceof PresetRoleError) {
    return fail(
      'ANIMATION_ROLE_MISMATCH',
      `${error.presetId} has a different role from effect ${error.effectId}.`,
      { presetId: error.presetId, effectId: error.effectId },
    );
  }
  return null;
}

/** Run a pure preset function, turning its preset errors into tool failures. */
function tryPreset<T>(work: () => T): T | Failure {
  try {
    return work();
  } catch (error) {
    const mapped = presetErrorFailure(error);
    if (mapped) {
      return mapped;
    }
    throw error;
  }
}

function isFailure(value: unknown): value is Failure {
  return Boolean(value) && typeof value === 'object' && 'failure' in (value as object);
}

function presetFor(presetId: unknown) {
  if (typeof presetId !== 'string' || !isAnimationPresetId(presetId)) {
    return fail(
      'ANIMATION_PRESET_UNKNOWN',
      `Unknown presetId ${JSON.stringify(presetId)}. Read capability topic animation for presets.`,
    );
  }
  return getAnimationPreset(presetId);
}

function findEffect(animation: Animation | null, effectId: unknown): AnimationEffect | Failure {
  if (typeof effectId !== 'string' || effectId === '') {
    return fail('INVALID_INPUT', 'effectId is required.');
  }
  const effect = animation?.effects?.find((candidate) => candidate.id === effectId);
  return effect ?? fail('ANIMATION_EFFECT_NOT_FOUND', `No animation effect has id ${effectId}.`);
}

function checkRevision(state: Record<string, unknown>, expected: unknown): Failure | null {
  if (expected === undefined) {
    return null;
  }
  if (typeof expected !== 'number' || !Number.isFinite(expected)) {
    return fail('INVALID_INPUT', 'expectedWorkingRevision must be a number.');
  }
  const current = readWorkingRevision(state);
  if (expected !== current) {
    return fail(
      'STALE_REVISION',
      `The working copy is at revision ${current}, not ${expected}. Read animation again.`,
      { expectedWorkingRevision: expected, workingRevision: current },
    );
  }
  return null;
}

function planApply(
  animation: Animation | null,
  names: Map<string, string>,
  input: Record<string, unknown>,
): Plan | Failure {
  const { targetId } = input;
  if (typeof targetId !== 'string' || targetId === '') {
    return fail('ANIMATION_TARGET_INVALID', 'apply_animation_effect requires targetId.');
  }
  if (!names.has(targetId)) {
    return fail('ANIMATION_TARGET_INVALID', `No element has id ${targetId} on the open template.`);
  }
  const preset = presetFor(input.presetId);
  if (isFailure(preset)) {
    return preset;
  }
  const params = validateEffectParams(preset, input.params);
  if ('error' in params) {
    return fail('ANIMATION_PARAMS_INVALID', params.error, params.details);
  }
  const next = tryPreset(() =>
    applyEffect(animation, { targetId, presetId: preset.id, params: params.params }),
  );
  if (isFailure(next)) {
    return next;
  }
  const before = animation?.effects ?? [];
  const kept = new Set((next.effects ?? []).map((effect) => effect.id));
  const replaced = before.filter((effect) => !kept.has(effect.id));
  const previousIds = new Set(before.map((effect) => effect.id));
  return {
    commit: (actions) => actions.applyAnimationEffect(targetId, preset.id, params.params),
    describe: (after, afterNames) => {
      const added = after?.effects?.find((effect) => !previousIds.has(effect.id));
      return {
        effect: after && added ? describeEffect(after, added, afterNames) : undefined,
        replaced:
          animation && replaced.length > 0
            ? replaced.map((effect) => describeEffect(animation, effect, afterNames))
            : [],
      };
    },
  };
}

function planUpdate(
  animation: Animation,
  existing: AnimationEffect,
  input: Record<string, unknown>,
): Plan | Failure {
  if (input.presetId === undefined && input.params === undefined) {
    return fail('INVALID_INPUT', 'update_animation_effect requires params or presetId.');
  }
  let presetId = existing.presetId;
  if (input.presetId !== undefined) {
    const preset = presetFor(input.presetId);
    if (isFailure(preset)) {
      return preset;
    }
    const changed = tryPreset(() => changeEffectPreset(animation, existing.id, preset.id));
    if (isFailure(changed)) {
      return changed;
    }
    presetId = preset.id;
  }
  const params = validateEffectParams(getAnimationPreset(presetId), input.params);
  if ('error' in params) {
    return fail('ANIMATION_PARAMS_INVALID', params.error, params.details);
  }
  const changePreset = presetId !== existing.presetId;
  const updateParams = Object.keys(params.params).length > 0;
  return {
    commit: (actions) => {
      const write = () => {
        if (changePreset) {
          actions.changeAnimationEffectPreset(existing.id, presetId);
        }
        if (updateParams) {
          actions.updateAnimationEffect(existing.id, params.params);
        }
      };
      if (actions.runHistoryBatch) {
        actions.runHistoryBatch(write);
      } else {
        write();
      }
    },
    describe: (after, names) => {
      const updated = after?.effects?.find((effect) => effect.id === existing.id);
      return { effect: after && updated ? describeEffect(after, updated, names) : undefined };
    },
  };
}

function planOperation(
  operation: AnimationOperation,
  state: Record<string, unknown>,
  input: Record<string, unknown>,
): Plan | Failure {
  const animation = readAnimation(state);
  if (operation === 'apply_animation_effect') {
    return planApply(animation, elementNames(state), input);
  }
  const existing = findEffect(animation, input.effectId);
  if (isFailure(existing)) {
    return existing;
  }
  // findEffect only succeeds when the animation holds the effect.
  const current = animation as Animation;
  if (operation === 'update_animation_effect') {
    return planUpdate(current, existing, input);
  }
  return {
    commit: (actions) => actions.removeAnimationEffect(existing.id),
    describe: (after, names) => ({
      removedEffectId: existing.id,
      effect: describeEffect(current, existing, names),
      animationCleared: after === null,
    }),
  };
}

export function runAnimationOperation(
  store: EditorStoreLike,
  operation: AnimationOperation,
  input: Record<string, unknown>,
): TemplateWebMCPToolResult<AnimationApplyData> {
  const state = store.getState();
  const stale = checkRevision(state, input.expectedWorkingRevision);
  if (stale) {
    return mcpToolResult(stale.failure);
  }
  const plan = planOperation(operation, state, input);
  if (isFailure(plan)) {
    return mcpToolResult(plan.failure);
  }
  plan.commit(state as unknown as AnimationActions);
  const after = store.getState();
  const animation = readAnimation(after);
  const warnings = animationWarnings(animation, after);
  return mcpToolResult(
    toolSuccess({
      operation,
      workingRevision: readWorkingRevision(after),
      ...plan.describe(animation, elementNames(after)),
      ...(warnings.length > 0 ? { warnings } : {}),
    }),
  );
}
